// src/components/SurveyRefill/SurveyModalRefill.js
import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Stack,
  RadioGroup,
  Radio,
  CheckboxGroup,
  Checkbox,
  Box,
  Center,
  HStack,
  Textarea,
  FormControl,
  FormLabel,
  Input,
} from '@chakra-ui/react'
import { endpoints } from '../../api/apiCommons'
import heroDefault from '../../assets/images/ami-survey-refill-hero.webp'
import SurveyBillingContacts from './SurveyBillingContacts'
import {
  SURVEY_CONTACTO,
  exclusiveOptionsByQuestion,
} from '../../utils/surveys'

// === BASE BFF: solo REACT_APP_BFF (ya incluye /wsEmpresa en tu .env) ===
const base = (process.env.REACT_APP_BFF || '').trim().replace(/\/+$/, '')

function tokenHdr() {
  return {
    token: localStorage.getItem('token') || '',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  }
}
async function getJSON(path) {
  const r = await fetch(`${base}/${path}`, {
    headers: tokenHdr(),
    cache: 'no-store',
  })
  if (!r.ok) {
    const msg = await r.text().catch(() => '')
    throw new Error(`HTTP ${r.status} ${msg}`)
  }
  return r.json()
}

/**
 * Mapea la definición del API a la estructura del modal.
 * Soporta:
 *  - Cabecera {Nombre, Descripcion}
 *  - Preguntas con campos: CodigoPregunta, Tipo ('SC'/'MC'), Titulo/Texto, Requerida/Obligatoria, Posicion/Paso/IdPregunta
 *  - Opciones dentro de cada pregunta: { Valor/CodigoOpcion, Etiqueta/Texto, Posicion }
 *  - (Opcional) arreglo raíz de Opciones (legacy)
 */
function mapDefinitionToQuestions(defOrWrapped) {
  const root = defOrWrapped?.data ?? defOrWrapped ?? {}

  const preguntasRaw = Array.isArray(root.Preguntas)
    ? root.Preguntas.slice()
    : []
  const opcionesRoot = Array.isArray(root.Opciones) ? root.Opciones.slice() : []

  // indexar opciones raíz por pregunta (legacy)
  const byPregunta = new Map()
  opcionesRoot.forEach((o) => {
    const codPreg = o.CodigoPregunta ?? o.codigoPregunta
    const key = String(codPreg || '')
    if (!key) return
    if (!byPregunta.has(key)) byPregunta.set(key, [])
    byPregunta.get(key).push({
      value: String(o.Valor ?? o.CodigoOpcion ?? ''),
      label: String(o.Etiqueta ?? o.Texto ?? ''),
      pos: o.Posicion ?? 0,
    })
  })

  // orden gral por Posicion/Paso/IdPregunta
  const posOf = (p) =>
    p.Posicion ?? p.Paso ?? p.IdPregunta ?? Number.MAX_SAFE_INTEGER
  preguntasRaw.sort((a, b) => posOf(a) - posOf(b))

  const total = preguntasRaw.length

  const mapped = preguntasRaw.map((p, idx) => {
    const codigo = String(p.CodigoPregunta || '')
    // opciones por pregunta: prioriza p.Opciones (nuevo BFF)
    let opts = []
    if (Array.isArray(p.Opciones) && p.Opciones.length) {
      opts = p.Opciones.slice()
        .sort((a, b) => (a.Posicion ?? 0) - (b.Posicion ?? 0))
        .map((o) => ({
          value: String(o.Valor ?? o.CodigoOpcion ?? ''),
          label: String(o.Etiqueta ?? o.Texto ?? ''),
        }))
    } else if (byPregunta.has(codigo)) {
      opts = byPregunta
        .get(codigo)
        .slice()
        .sort((a, b) => a.pos - b.pos)
        .map(({ value, label }) => ({ value, label }))
    }

    const tipo = String(p.Tipo || '').toUpperCase()
    let kind = 'single'
    if (tipo === 'MC') kind = 'multi'
    else if (tipo === 'TX') kind = 'text'

    return {
      id: codigo, // clave por Código de Pregunta
      stepLabel: `Pregunta ${idx + 1} de ${total}`,
      title: String(p.Titulo ?? p.Texto ?? ''),
      kind,
      options: opts,
      required: Boolean(p.Requerida ?? p.Obligatoria ?? false),
    }
  })

  return {
    nombre: root.Nombre ?? null,
    descripcion: root.Descripcion ?? null,
    preguntas: mapped,
  }
}

function answerText(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return String(value.ValorTexto ?? value.text ?? '')
  }
  return String(value ?? '')
}

function isScaleQuestion(q) {
  return (
    q.kind === 'single' &&
    q.options.length >= 5 &&
    q.options.every((o) => /^\d+$/.test(String(o.label ?? '').trim()))
  )
}

export default function SurveyModalRefill({
  isOpen,
  onClose,
  onSubmitAnswers,
  companyId,
  surveyCode = SURVEY_CONTACTO,
  hero,
  palette,
}) {
  // ---- defaults + merge ----
  const defaultHero = {
    title: '¡Hola!',
    body1: 'Queremos saber si nuestros otros servicios podrían ayudarte.',
    body2: 'Favor responder esta encuesta, te tomará menos de 1 minuto.',
    cta: 'Responder aquí',
    imageUrl: heroDefault,
  }
  const defaultPalette = {
    brandBg: '#2A1E63',
    brandAccent: '#6B56C4',
    brandTextOnDark: 'white',
    buttonHover: '#5B47B1',
  }
  const H = { ...defaultHero, ...(hero || {}) }
  const P = { ...defaultPalette, ...(palette || {}) }
  const isBillingSurvey = surveyCode === 'EMPRESAS_NO_NUEVAS_2026'

  // ---- Definición desde el server ----
  const [header, setHeader] = useState({ nombre: null, descripcion: null })
  const [questions, setQuestions] = useState([])
  const [loadingDef, setLoadingDef] = useState(false)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function loadDef() {
      try {
        setLoadingDef(true)
        setLoadError(null)
        const def = await getJSON(
          `${endpoints.surveyDefinicion}?CodigoSurvey=${encodeURIComponent(
            surveyCode,
          )}&_ts=${Date.now()}`,
        )
        const mapped = mapDefinitionToQuestions(def)
        if (cancelled) return
        setHeader({ nombre: mapped.nombre, descripcion: mapped.descripcion })
        setQuestions(mapped.preguntas)
      } catch (e) {
        if (!cancelled)
          setLoadError(e?.message || 'No se pudo cargar la encuesta')
      } finally {
        if (!cancelled) setLoadingDef(false)
      }
    }
    if (isOpen) { setStep(0); loadDef() }
    return () => {
      cancelled = true
    }
  }, [isOpen, surveyCode])

  // ---- state / helpers ----
  const [step, setStep] = useState(0)

  const initialAnswers = useMemo(
    () =>
      Object.fromEntries(
        questions.map((q) => [q.id, q.kind === 'multi' ? [] : '']),
      ),
    [questions],
  )

  const [answers, setAnswers] = useState(initialAnswers)

  useEffect(() => {
    setAnswers(initialAnswers)
  }, [initialAnswers]) // reinicia si cambian preguntas

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // === Encuesta especial de actualización de contacto ===
  const contactFieldIds = ['P2_NOMBRE', 'P2_CARGO', 'P2_EMAIL', 'P2_FONO']

  const isContactSurvey = useMemo(() => {
    if (!questions.length) return false
    const ids = new Set(questions.map((q) => q.id))
    return ids.has('P1_CAMBIOS') && contactFieldIds.every((id) => ids.has(id))
  }, [questions])

  // Pasos lógicos:
  // - Encuesta normal  => tantas preguntas como vengan del API
  // - Encuesta contacto => 2 pasos lógicos: P1_CAMBIOS y slide de contacto
  const LOGICAL_TOTAL = isContactSurvey ? 2 : questions.length
  const FINAL_STEP = LOGICAL_TOTAL + 1

  const isLastQuestionStep = step === LOGICAL_TOTAL
  const isFinalStep = step === FINAL_STEP

  function isOtherOption(opt) {
    if (!opt) return false
    const v = String(opt.value || '').toUpperCase()
    const lbl = String(opt.label || '')
    return v === 'OTHER' || /otro\s*\(.*especificar.*\)/i.test(lbl)
  }

  // Devuelve la "pregunta lógica" a mostrar en el paso actual
  const getQuestionForStep = (currentStep) => {
    if (currentStep <= 0 || currentStep > LOGICAL_TOTAL) return null

    // Encuestas normales: 1 pregunta por slide
    if (!isContactSurvey) {
      const base = questions[currentStep - 1]
      if (!base) return null
      return {
        ...base,
        stepLabel: `Pregunta ${currentStep} de ${LOGICAL_TOTAL}`,
      }
    }

    // Encuesta UPD_CONTACTO_2025:
    // Paso 1 => P1_CAMBIOS (SC)
    if (currentStep === 1) {
      const base = questions.find((q) => q.id === 'P1_CAMBIOS')
      if (!base) return null
      return {
        ...base,
        stepLabel: 'Pregunta 1 de 2',
      }
    }

    // Paso 2 => slide unificada de contacto (4 campos)
    if (currentStep === 2) {
      const titleBase =
        questions.find((q) => q.id === 'P2_NOMBRE')?.title ||
        'En relación a la persona que toma decisiones sobre la contratación o incorporación de nuevos beneficios, favor completar sus datos de contacto.'

      return {
        id: 'P2_CONTACTO',
        kind: 'contact',
        required: false,
        stepLabel: 'Pregunta 2 de 2',
        title: titleBase,
        fields: [
          {
            id: 'P2_NOMBRE',
            label: 'Nombre y Apellido',
            placeholder: 'Nombre y Apellido',
          },
          {
            id: 'P2_CARGO',
            label: 'Cargo',
            placeholder: 'Cargo',
          },
          {
            id: 'P2_EMAIL',
            label: 'Correo electrónico',
            placeholder: 'correo@ejemplo.cl',
          },
          {
            id: 'P2_FONO',
            label: 'Teléfono / Celular',
            placeholder: '56 912345678',
          },
        ],
      }
    }

    return null
  }

  // Validación del paso actual
  const isCurrentStepValid = useMemo(() => {
    if (step === 0 || isFinalStep) return true

    const q = getQuestionForStep(step)
    if (!q) return true

    // --- Validación especial para slide de contacto ---
    if (isContactSurvey && q.id === 'P2_CONTACTO') {
      // Helper para leer ValorTexto si viene como objeto
      const getText = (qid) => {
        const v = answers[qid]
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          return String(v.ValorTexto ?? v.text ?? '').trim()
        }
        return String(v ?? '').trim()
      }

      const nombre = getText('P2_NOMBRE')
      const cargo = getText('P2_CARGO')
      const email = getText('P2_EMAIL')
      const fono = getText('P2_FONO')

      // 1) Nombre y cargo obligatorios
      if (!nombre || !cargo) return false

      // 2) Email obligatorio y con formato correcto:
      //    - algo@algo.algo
      const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
      if (!emailOk) return false

      // 1) Teléfono: opcional, pero si lo ingresa debe ser válido
      //    Regla:
      //      - Se toman solo dígitos
      //      - Si está vacío => OK
      //      - Si parte con 56  => debe tener 56 + 8 dígitos (10 en total)
      //      - Si no parte con 56 => debe tener 8 dígitos (número local)
      const digits = fono.replace(/\D/g, '')

      if (digits.length === 0) {
        // Teléfono vacío: permitido
        return true
      }

      if (digits.startsWith('56')) {
        // incluye prefijo país 56 + 8 dígitos
        if (digits.length !== 10) return false
      } else {
        // sin prefijo, exigimos 8 dígitos
        if (digits.length !== 8) return false
      }

      return true
    }

    // --- Lógica antigua: Q1_BENEFICIO / Q2_VALORACION (otra encuesta) ---
    if (q.id === 'Q2_VALORACION') {
      const q1 = answers['Q1_BENEFICIO']
      if (q1 === 'ALL' || q1 === 'SOME') return true
    }

    if (q.kind === 'text') {
      if (!q.required) return true
      return answerText(answers[q.id]).trim().length > 0
    }

    if (!q.required) return true
    const val = answers[q.id]

    // Si la opción seleccionada es OTHER, exigir texto
    if (q.kind === 'single') {
      const selected = typeof val === 'object' ? val?.ValorOpcion : val
      if (String(selected || '').toUpperCase() === 'OTHER') {
        const txt = (
          typeof val === 'object' ? val?.ValorTexto || '' : ''
        ).trim()
        return txt.length > 0
      }
    }

    if (q.kind === 'multi') return Array.isArray(val) && val.length > 0
    return !!val
  }, [step, answers, isFinalStep, isContactSurvey, questions])

  const updateAnswer = (qid, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [qid]: value }
      // Lógica antigua: si Q1 pasa a ALL/SOME, Q2 deja de aplicar
      if (qid === 'Q1_BENEFICIO' && (value === 'ALL' || value === 'SOME')) {
        delete next['Q2_VALORACION']
      }
      return next
    })
  }

  const updateMultiAnswer = (qid, values) => {
    const exclusives = exclusiveOptionsByQuestion[qid] || []
    if (!exclusives.length) {
      updateAnswer(qid, values)
      return
    }

    const prev = Array.isArray(answers[qid]) ? answers[qid] : []
    const addedExclusive = values.filter(
      (v) => !prev.includes(v) && exclusives.includes(v),
    )

    if (addedExclusive.length) {
      updateAnswer(qid, addedExclusive)
      return
    }

    updateAnswer(
      qid,
      values.filter((v) => !exclusives.includes(v)),
    )
  }

  const submitAndFinish = async () => {
    try {
      setSubmitting(true)
      setSubmitError('')
      // Enviar solo preguntas aplicables (excluir Q2 si Q1 = ALL/SOME)
      const cleaned = {}
      const q1 = answers['Q1_BENEFICIO']
      const skipQ2 = q1 === 'ALL' || q1 === 'SOME'
      for (const q of questions) {
        if (skipQ2 && q.id === 'Q2_VALORACION') continue
        const v = answers[q.id]
        if (
          v !== undefined &&
          v !== '' &&
          !(Array.isArray(v) && v.length === 0)
        ) {
          cleaned[q.id] = v
        }
      }
      if (onSubmitAnswers) await onSubmitAnswers(cleaned)
      setStep(FINAL_STEP) // solo registro exitoso
    } catch (e) {
      setSubmitError(e?.message || 'No fue posible registrar la encuesta. Reintenta.')
    } finally {
      setSubmitting(false)
    }
  }

  // === Reglas de salto ===
  const goNext = async () => {
    if (step === 0) {
      // aún cargando → no avances
      if (loadingDef || !questions.length) return
      setStep(1)
      return
    }

    // Lógica especial para encuesta de contacto
    if (isContactSurvey) {
      if (step === 1) {
        const a1 = answers['P1_CAMBIOS']
        // Si NO hubo cambios => se termina la encuesta
        if (a1 === 'NO') {
          await submitAndFinish()
          return
        }
      }
    } else {
      // Lógica antigua Q1/Q2
      const idQ1 = 'Q1_BENEFICIO'
      const idQ2 = 'Q2_VALORACION'
      const a1 = answers[idQ1]

      // desde Q1 con ALL/SOME => saltar Q2 (ir a Q3)
      if (step === 1 && (a1 === 'ALL' || a1 === 'SOME')) {
        if (questions[1]?.id === idQ2) {
          setStep(3) // Q3 es el tercer paso (1-based)
          return
        }
      }
    }

    if (isLastQuestionStep) {
      await submitAndFinish()
      return
    }
    setStep((s) => Math.min(s + 1, FINAL_STEP))
  }

  const goPrev = () => {
    if (step === 0) return

    // Solo aplica a la encuesta antigua Q1/Q2
    if (!isContactSurvey) {
      const idQ1 = 'Q1_BENEFICIO'
      const idQ2 = 'Q2_VALORACION'
      const a1 = answers[idQ1]
      // si venimos de salto (Q1→Q3), volver a Q1 desde Q3
      if (
        step === 3 &&
        (a1 === 'ALL' || a1 === 'SOME') &&
        questions[1]?.id === idQ2
      ) {
        setStep(1)
        return
      }
    }

    setStep((s) => Math.max(0, s - 1))
  }

  // ---- UI ----
  const renderPortada = () => (
    <Box position="relative" minH="520px" overflow="hidden" bg={P.brandBg}>
      {H.imageUrl && (
        <Box
          position="absolute"
          inset="0"
          bgImage={`url(${H.imageUrl})`}
          bgSize="cover"
          bgPos="center"
          zIndex={0}
        />
      )}
      {H.imageUrl && (
        <Box position="absolute" inset="0" bg="rgba(0,0,0,0.55)" zIndex={1} />
      )}

      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom="88px"
        zIndex={2}
        px={{ base: 6, md: 10 }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <Box maxW="520px">
          <Text
            fontSize={{ base: '3xl', md: '4xl' }}
            fontWeight="extrabold"
            lineHeight="1.3"
            letterSpacing="-0.01em"
            mb={{ base: 2, md: 3 }}
            color="white" // << fuerza texto blanco
          >
            {H.title}
          </Text>

          <Stack spacing={{ base: 3, md: 4 }}>
            {H.body1 && (
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                lineHeight="1.6"
                color="white" // << blanco
              >
                {H.body1}
              </Text>
            )}
            {H.body2 && (
              <Text
                fontSize={{ base: 'md', md: 'xl' }}
                lineHeight="1.6"
                color="white" // << blanco
              >
                {H.body2}
              </Text>
            )}
          </Stack>
        </Box>
      </Box>

      <Box
        position="absolute"
        right={{ base: 4, md: 6 }}
        bottom={{ base: 4, md: 6 }}
        zIndex={3}
      >
        <Button
          borderRadius="30px"
          h="56px"
          px={8}
          minW="210px"
          bg={P.brandAccent}
          color="white"
          fontSize={{ base: 'md', md: 'lg' }}
          fontWeight="bold"
          onClick={goNext}
          _hover={{ backgroundColor: P.buttonHover }}
          border="none"
          boxShadow="none"
        >
          {H.cta}
        </Button>
      </Box>
    </Box>
  )

  const renderQuestion = (q) => {
    if (!q) return renderLoadingOrError()

    // Slide especial de contacto (4 campos en una sola pantalla)
    if (q.kind === 'contact') {
      return (
        <Box
          position="relative"
          bg={P.brandBg}
          color="white"
          px={{ base: 6, md: 10 }}
          py={{ base: 8, md: 10 }}
          minH="420px"
        >
          <Text color="whiteAlpha.800" mb={2}>
            {q.stepLabel}
          </Text>
          <Text fontWeight="extrabold" fontSize="2xl" mb={8} lineHeight="1.3">
            {q.title}
          </Text>

          <Stack spacing={4}>
            {q.fields.map((field) => {
              const raw = answers[field.id]
              const current =
                typeof raw === 'object' && raw !== null
                  ? raw.ValorTexto ?? ''
                  : raw ?? ''

              const handleChange = (e) => {
                let t = e.target.value

                // NOMBRE y CARGO siempre en mayúsculas
                if (field.id === 'P2_NOMBRE' || field.id === 'P2_CARGO') {
                  t = t.toUpperCase()
                }

                // TELÉFONO: quedarnos solo con números
                // - El usuario puede escribir "+56 99999999"
                // - Nosotros guardamos "5699999999"
                if (field.id === 'P2_FONO') {
                  let digits = t.replace(/\D/g, '') // quitamos +, espacios, guiones, etc.
                  if (digits.length > 10) {
                    digits = digits.slice(0, 10) // 56 + 8 dígitos máx
                  }
                  t = digits
                }

                // Guardamos SIEMPRE como objeto con ValorTexto
                updateAnswer(field.id, { ValorTexto: t })
              }

              return (
                <FormControl key={field.id}>
                  <FormLabel color="white" fontWeight="bold">
                    {field.label}
                  </FormLabel>
                  <Input
                    value={current}
                    onChange={handleChange}
                    placeholder={field.placeholder || ''}
                    bg="whiteAlpha.100"
                    _placeholder={{ color: 'whiteAlpha.700' }}
                    color="white"
                  />
                  {field.helper && (
                    <Text fontSize="sm" mt={1} color="whiteAlpha.800">
                      {field.helper}
                    </Text>
                  )}
                </FormControl>
              )
            })}
          </Stack>
        </Box>
      )
    }

    if (q.kind === 'text') {
      const current = answerText(answers[q.id])
      return (
        <Box
          position="relative"
          bg={P.brandBg}
          color="white"
          px={{ base: 6, md: 10 }}
          py={{ base: 8, md: 10 }}
          minH="420px"
        >
          <Text color="whiteAlpha.800" mb={2}>
            {q.stepLabel}
          </Text>
          <Text fontWeight="extrabold" fontSize="2xl" mb={8} lineHeight="1.3">
            {q.title}
          </Text>

          <FormControl>
            <Textarea
              value={current}
              maxLength={1500}
              rows={6}
              onChange={(e) =>
                updateAnswer(q.id, { ValorTexto: e.target.value })
              }
              placeholder="Escribe aquí (máx. 1500 caracteres)"
              bg="whiteAlpha.100"
              _placeholder={{ color: 'whiteAlpha.700' }}
              color="white"
            />
            <Text fontSize="sm" mt={2} textAlign="right" color="whiteAlpha.800">
              {current.length}/1500
            </Text>
          </FormControl>
        </Box>
      )
    }

    // Resto de encuestas: comportamiento actual
    return (
      <Box
        position="relative"
        bg={P.brandBg}
        color="white"
        px={{ base: 6, md: 10 }}
        py={{ base: 8, md: 10 }}
        minH="420px"
      >
        <Text color="whiteAlpha.800" mb={2}>
          {q.stepLabel}
        </Text>
        <Text fontWeight="extrabold" fontSize="2xl" mb={8} lineHeight="1.3">
          {q.title}
        </Text>

        {q.kind === 'single' ? (
          <>
            <RadioGroup
              value={
                typeof answers[q.id] === 'object'
                  ? answers[q.id]?.ValorOpcion
                  : answers[q.id]
              }
              onChange={(v) => {
                // si selecciona OTHER, preserva texto existente
                if (String(v).toUpperCase() === 'OTHER') {
                  const prevTxt =
                    typeof answers[q.id] === 'object'
                      ? answers[q.id]?.ValorTexto || ''
                      : ''
                  updateAnswer(q.id, {
                    ValorOpcion: 'OTHER',
                    ValorTexto: prevTxt,
                  })
                } else {
                  updateAnswer(q.id, v)
                }
              }}
            >
              <Stack
                spacing={isScaleQuestion(q) ? 4 : 5}
                direction={isScaleQuestion(q) ? 'row' : 'column'}
                wrap={isScaleQuestion(q) ? 'wrap' : undefined}
              >
                {q.options.map((opt) => (
                  <Radio
                    key={opt.value}
                    value={opt.value}
                    colorScheme="purple"
                    sx={{
                      '.chakra-radio__label': { color: 'white' },
                      '.chakra-radio__control': {
                        borderColor: 'whiteAlpha.800',
                      },
                    }}
                  >
                    {opt.label}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>

            {/* Si eligió OTHER, mostrar textarea */}
            {(() => {
              const val = answers[q.id]
              const selected = typeof val === 'object' ? val?.ValorOpcion : val
              if (String(selected || '').toUpperCase() !== 'OTHER') return null
              return (
                <Box mt={4}>
                  <FormControl>
                    <FormLabel color="white" fontWeight="bold">
                      Especifica
                    </FormLabel>
                    <Textarea
                      value={val?.ValorTexto ?? ''}
                      maxLength={1500}
                      onChange={(e) => {
                        const t = e.target.value
                        updateAnswer(q.id, {
                          ValorOpcion: 'OTHER',
                          ValorTexto: t,
                        })
                      }}
                      placeholder="Escribe aquí (máx. 1500 caracteres)"
                      bg="whiteAlpha.100"
                      _placeholder={{ color: 'whiteAlpha.700' }}
                      color="white"
                    />
                    <Text
                      fontSize="sm"
                      mt={2}
                      textAlign="right"
                      color="whiteAlpha.800"
                    >
                      {(val?.ValorTexto ?? '').length}/1500
                    </Text>
                  </FormControl>
                </Box>
              )
            })()}
          </>
        ) : (
          // MULTI (sin "Other" en este survey)
          <CheckboxGroup
            value={answers[q.id]}
            onChange={(vals) => updateMultiAnswer(q.id, vals)}
          >
            <Stack spacing={5}>
              {q.options.map((opt) => (
                <Checkbox
                  key={opt.value}
                  value={opt.value}
                  colorScheme="purple"
                  sx={{
                    '.chakra-checkbox__label': { color: 'white' },
                    '.chakra-checkbox__control': {
                      borderColor: 'whiteAlpha.800',
                    },
                  }}
                >
                  {opt.label}
                </Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
        )}
      </Box>
    )
  }

  const renderGracias = () => (
    <Center
      flexDir="column"
      bg={P.brandBg}
      color={P.brandTextOnDark}
      minH="420px"
      textAlign="center"
      px={8}
      py={12}
    >
      <Text fontSize="3xl" fontWeight="bold" mb={4}>
        ¡Gracias por tu tiempo y respuestas!
      </Text>
      <Text fontSize="lg" maxW="740px" mb={8} opacity={0.95}>
        Tu opinión es muy valiosa para seguir mejorando.
        <br />
        Ahora puedes continuar utilizando la plataforma con normalidad.
      </Text>
      <Button
        w="200px"
        h="48px"
        borderRadius="30px"
        bg={P.brandAccent}
        color="white"
        _hover={{ backgroundColor: P.buttonHover }}
        onClick={onClose}
      >
        Cerrar
      </Button>
    </Center>
  )

  // Fallback de carga/error cuando ya estamos en pasos > 0
  const renderLoadingOrError = () => (
    <Box
      bg={P.brandBg}
      color="white"
      minH="260px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={8}
    >
      <Text opacity={0.9}>
        {loadError ? `Error: ${loadError}` : 'Cargando encuesta…'}
      </Text>
    </Box>
  )

  if (!isOpen) return null
  if (loadingDef && step === 0)
    return (
      <Modal
        isOpen
        onClose={() => {}}
        isCentered
        size="2xl"
        blockScrollOnMount
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent borderRadius="15px" overflow="hidden">
          <ModalBody p={8}>Cargando encuesta…</ModalBody>
        </ModalContent>
      </Modal>
    )
  if (loadError && step === 0)
    return (
      <Modal
        isOpen
        onClose={() => {}}
        isCentered
        size="2xl"
        blockScrollOnMount
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent borderRadius="15px" overflow="hidden">
          <ModalBody p={8}>Error: {loadError}</ModalBody>
        </ModalContent>
      </Modal>
    )

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      isCentered
      size="2xl"
      blockScrollOnMount
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent borderRadius="15px" overflow="hidden">
        <ModalHeader p={0} />
        <ModalBody p={0}>
          {step === 0 && renderPortada()}

          {step > 0 && !isBillingSurvey &&
            !isFinalStep &&
            (!loadingDef && !loadError && getQuestionForStep(step)
              ? renderQuestion(getQuestionForStep(step))
              : renderLoadingOrError())}

          {step > 0 && isBillingSurvey && !loadingDef && !loadError && (
            <SurveyBillingContacts
              key={surveyCode}
              questions={questions}
              onSubmitAnswers={onSubmitAnswers}
              onClose={onClose}
            />
          )}
          {isFinalStep && !isBillingSurvey && renderGracias()}
        </ModalBody>

        {step > 0 &&
          !isBillingSurvey &&
		  !isFinalStep &&
		  questions.length > 0 &&
		  !loadingDef &&
		  !loadError && (
			<ModalFooter bg={P.brandBg} pt={4} pb={6}>
			  {submitError && (
				<Text color="red.300" px={3} mb={2}>
				  {submitError}
				</Text>
			  )}

			  <HStack w="100%" justify="space-between">
				<Button
				  borderRadius="30px"
				  h="44px"
				  minW="160px"
				  px={6}
				  bg="whiteAlpha.200"
				  color="whiteAlpha.900"
				  _hover={{ bg: 'whiteAlpha.300' }}
				  onClick={goPrev}
				  isDisabled={step === 1}
				>
				  Atrás
				</Button>

				<Button
				  borderRadius="30px"
				  h="44px"
				  minW="160px"
				  px={6}
				  bg={P.brandAccent}
				  color="white"
				  _hover={{ backgroundColor: P.buttonHover }}
				  onClick={goNext}
				  isDisabled={!isCurrentStepValid}
				  isLoading={submitting}
				>
				  {isLastQuestionStep ? 'Enviar' : 'Siguiente'}
				</Button>
			  </HStack>
			</ModalFooter>
		  )}
      </ModalContent>
    </Modal>
  )
}

SurveyModalRefill.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitAnswers: PropTypes.func,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  surveyCode: PropTypes.string,
  hero: PropTypes.object,
  palette: PropTypes.object,
}
